import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserShield, FaSearch,
  FaFileAlt, FaEye, FaSyncAlt, FaClock, FaBell, FaDownload,
  FaCheckCircle
} from "react-icons/fa";
import { useTheme } from "../../context/SocketContext";
import { useRealtimeRefresh } from "../../context/RealtimeSyncContext";
import FilePreviewModal from "../Tax Filling/FilePreviewModal";
import PageHeader from "../../components/PageHeader";
import { FullPageSkeleton } from "../../components/ui/Skeleton";
import EmptyState from "../../components/ui/EmptyState";
import { usePortalContent } from "../../portal";

const RegisteredAgentPage = () => {
  const { currentThemeColors } = useTheme();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [registeredAgentService, setRegisteredAgentService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const { content: portalContent } = usePortalContent('registered-agent');

  const pageTitle = portalContent?.pageTitle || "Registered Agent";
  const pageSubtitle = portalContent?.pageSubtitle || "Manage and view your registered agent documents";
  const renewalCardTitle = portalContent?.renewalCardTitle || "Registered Agent Renewal Due Date";
  const renewalButtonText = portalContent?.renewalButtonText || "Pay $125 Now";
  const renewalButtonLink = portalContent?.renewalButtonLink || "/pricing?service=service_7&step=2";
  const documentsCardTitle = portalContent?.documentsCardTitle || "Your Registered Agent Documents";
  const tableHeaderDocumentName = portalContent?.tableHeaderDocumentName || "Document Name";
  const tableHeaderDate = portalContent?.tableHeaderDate || "Date";
  const tableHeaderActions = portalContent?.tableHeaderActions || "Actions";
  const emptyStateTitle = portalContent?.emptyStateTitle || "No Documents Found";
  const emptyStateDescription = portalContent?.emptyStateDescription || "No registered agent documents have been uploaded yet";
  const emptyStateSearchDescription = portalContent?.emptyStateSearchDescription || "Try adjusting your search criteria";
  const clearSearchLabel = portalContent?.clearSearchLabel || "Clear Search";

  const fetchRegisteredAgentDocuments = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch documents");

      const data = await response.json();
      setDocuments(data.user.registeredAgentDocuments || []);
      setRegisteredAgentService(data.user.registeredAgentService || null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegisteredAgentDocuments();
  }, [fetchRegisteredAgentDocuments]);

  useRealtimeRefresh(['registered-agent', 'profile'], fetchRegisteredAgentDocuments);

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Calculate days remaining until due date
  const getDaysRemaining = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get urgency color based on days remaining
  const getUrgencyStyle = (daysRemaining) => {
    if (daysRemaining === null) return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-300 dark:border-gray-600' };
    if (daysRemaining <= 7) return { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', border: 'border-red-300 dark:border-red-600' };
    if (daysRemaining <= 30) return { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-300 dark:border-orange-600' };
    if (daysRemaining <= 90) return { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-300 dark:border-yellow-600' };
    return { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', border: 'border-green-300 dark:border-green-600' };
  };

  const handlePreview = async (document) => {
    setPreviewFile({
      url: document.fileUrl,
      name: document.originalName,
      size: document.size,
      type: document.originalName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg'
    });

    await markAsRead(document._id, document.read);
  };

  const markAsRead = async (documentId, isAlreadyRead) => {
    if (isAlreadyRead) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/mark-registered-agent-document-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ documentId })
      });
      
      setDocuments(prev => prev.map(doc => 
        doc._id === documentId ? { ...doc, read: true } : doc
      ));
    } catch (error) {
      console.error("Error marking document as read:", error);
    }
  };

  const handleDownload = async (document) => {
    await markAsRead(document._id, document.read);
    const downloadUrl = `${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`;
    window.open(downloadUrl, '_blank');
  };



  const filteredDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.originalName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.originalName.localeCompare(b.originalName);
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

  if (loading) {
    return (
      <div className="space-y-6">
        <FullPageSkeleton message="Loading documents..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 inline-block">
          <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>
          <button onClick={fetchRegisteredAgentDocuments} className="text-primary-500 hover:text-primary-600 font-medium">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FaUserShield}
        title={pageTitle}
        subtitle={pageSubtitle}
      />

      {/* Due Date Cards (supports up to 2 states/due dates) */}
      {(() => {
        const activeRenewalSlots = [];
        if (registeredAgentService?.dueDate) {
          activeRenewalSlots.push({
            slot: 1,
            state: registeredAgentService.state,
            dueDate: registeredAgentService.dueDate,
            paymentStatus: registeredAgentService.paymentStatus || 'unpaid',
            paidAt: registeredAgentService.paidAt,
          });
        }
        if (registeredAgentService?.dueDate2) {
          activeRenewalSlots.push({
            slot: 2,
            state: registeredAgentService.state2,
            dueDate: registeredAgentService.dueDate2,
            paymentStatus: registeredAgentService.paymentStatus2 || 'unpaid',
            paidAt: registeredAgentService.paidAt2,
          });
        }

        if (activeRenewalSlots.length === 0) return null;

        return (
          <div className="space-y-4">
            <style>{`
              @keyframes shimmer-streak {
                0% { transform: translateX(-150%) skewX(-15deg); }
                100% { transform: translateX(250%) skewX(-15deg); }
              }
              @keyframes pulse-glow {
                0%, 100% { box-shadow: 0 0 15px var(--glow-color); }
                50% { box-shadow: 0 0 25px var(--glow-color); }
              }
              .shimmer-streak-active {
                animation: shimmer-streak 2.5s infinite linear;
              }
              .pulse-glow-active {
                animation: pulse-glow 2s infinite ease-in-out;
              }
            `}</style>
            {activeRenewalSlots.map((item, idx) => {
              const daysRemaining = getDaysRemaining(item.dueDate);
              const urgencyStyle = getUrgencyStyle(daysRemaining);
              const paymentLink = `/pricing?service=service_7&step=2${item.state ? `&state=${encodeURIComponent(item.state)}` : ''}&slot=${item.slot}`;

              return (
                <div
                  key={item.slot}
                  className="opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${50 * (idx + 1)}ms`, animationFillMode: 'forwards' }}
                >
                  <div className={`card p-3 md:p-4 ${urgencyStyle.bg} border-2 ${urgencyStyle.border}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${urgencyStyle.bg} border ${urgencyStyle.border} shrink-0`}>
                          <FaClock className={`w-5 h-5 ${urgencyStyle.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <h3 className="text-base font-bold text-dark-800 dark:text-white">{renewalCardTitle}</h3>
                            {item.state && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 border border-primary-200 dark:border-primary-800/40">
                                {item.state}
                              </span>
                            )}
                            {item.paymentStatus === 'paid' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800/40">
                                <FaCheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" /> Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40">
                                Unpaid
                              </span>
                            )}
                          </div>
                          <p className={`text-xl font-bold ${urgencyStyle.text}`}>
                            {formatDateForDisplay(item.dueDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-1.5 rounded-lg ${urgencyStyle.bg} border ${urgencyStyle.border}`}>
                            <span className={`text-base font-bold ${urgencyStyle.text}`}>
                              {daysRemaining !== null ? (
                                daysRemaining > 0 ? (
                                   <>{daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining</>
                                ) : daysRemaining === 0 ? (
                                  <>Due Today!</>
                                ) : (
                                  <>{Math.abs(daysRemaining)} day{Math.abs(daysRemaining) !== 1 ? 's' : ''} overdue</>
                                )
                              ) : 'N/A'}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate(paymentLink)}
                            className="group relative overflow-hidden px-6 py-2 rounded-xl text-white font-bold transition-all duration-300 hover:scale-105 shadow-lg pulse-glow-active"
                            style={{ 
                              background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`,
                              boxShadow: `0 0 15px var(--glow-color), 0 4px 6px -1px rgba(0,0,0,0.1)`
                            }}
                          >
                            <span className="relative z-10">{item.paymentStatus === 'paid' ? 'Renew Again ($125)' : renewalButtonText}</span>
                            <div className="absolute inset-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/40 to-transparent shimmer-streak-active"></div>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex-1 relative">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-3 rounded-xl bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-dark-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
        >
          <option value="date">Sort by Date</option>
          <option value="name">Sort by Name</option>
        </select>
      </div>

      {/* Documents Table */}
      <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center gap-2">
          <FaUserShield className="w-5 h-5 text-primary-500" />
          <h2 className="font-semibold text-dark-800 dark:text-white">{documentsCardTitle}</h2>
        </div>
        
        {filteredDocuments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{tableHeaderDocumentName}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{tableHeaderDate}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{tableHeaderActions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                {filteredDocuments.map((document, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-dark-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FaFileAlt className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-dark-800 dark:text-white">{document.originalName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{new Date(document.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {document.read ? (
                          <span 
                            onClick={() => handlePreview(document)}
                            className="px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-xs font-bold uppercase border border-green-100 dark:border-green-800/30 tracking-tight cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                          >
                            Read
                          </span>
                        ) : (
                          <span 
                            onClick={() => handlePreview(document)}
                            className="px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 text-xs font-bold uppercase border border-orange-100 dark:border-orange-800/30 tracking-tight cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                          >
                            Unread
                          </span>
                        )}
                        <button
                          onClick={() => handleDownload(document)}
                          className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="Download"
                        >
                          <FaDownload className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={FaUserShield}
            title={emptyStateTitle}
            description={searchTerm
              ? emptyStateSearchDescription
              : emptyStateDescription}
            containerClassName="p-12 text-center"
            descriptionClassName="text-gray-500 dark:text-gray-400 mb-4"
          >
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 rounded-xl border border-primary-500 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                {clearSearchLabel}
              </button>
            )}
          </EmptyState>
        )}
      </div>

      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export default RegisteredAgentPage;
